import Utils from '../services/Utils';

// Single source of truth for Kronos navigation.
//
// Every route that should appear in the sidebar and/or the command palette is
// declared here once, with enough metadata for both consumers: sidebar rendering
// (icon, ability checks, module gating), command-palette search (keywords,
// description i18n key) and breadcrumb display (section › group › item).
//
// Anything added here becomes visible in both the sidebar and the command
// palette automatically, provided its `visible` predicates are satisfied.
//
// Shape:
//   NAVIGATION: [
//     {
//       sectionId,        // Utils.SECTION_*
//       groups: [
//         {
//           id,           // stable id used by sidebar collapse state
//           labelKey,     // i18n key for the heading
//           iconClass,    // optional FA class for the heading
//           visible,      // (ctx) => boolean (falsy -> group hidden entirely)
//           items: [ { id, path, labelKey, labelOptions?, iconClass, visible, descKey?, keywords?, sidebarHidden? }, ... ]
//           sidebarHidden: when true, item is omitted from the sidebar but still indexed for the command palette and breadcrumbs.
//           dynamicItems, // optional (ctx) => Array<item> – appended after static items
//         }, ...
//       ]
//     }, ...
//   ]
//
// Context passed to visibility predicates:
//   { user, tkt_instance, setting, workflows, pathname, eventival }
//
// Predicate helpers (composable): can(...) / anyCan(...) / hasModule(...) /
// anyModule(...) / and(...) / or(...) / not(...).

// Resolve a "<resource>:<action>" shorthand the same way the <Ability can="...">
// component does. In particular "<resource>:any" matches when the user has any
// truthy right on <resource>, which `user.can()` does not natively handle.
function splitAbility(spec) {
    const [resource, action] = String(spec).split(':');
    return { resource, action };
}

export function can(spec) {
    const { resource, action } = splitAbility(spec);
    return ({ user }) => {
        if (!user?.abilities)
            return false;
        if (user.abilities.superadmin)
            return true;
        if (action === 'any') {
            const bucket = user.abilities[resource];
            if (!bucket)
                return false;
            return Object.values(bucket).some(Boolean);
        }
        return !!user.can?.(action, resource);
    };
}

export function anyCan(specs) {
    const fns = specs.map(can);
    return ctx => fns.some(fn => fn(ctx));
}

export function hasModule(mod) {
    return ({ tkt_instance }) => !!tkt_instance?.hasModule?.(mod);
}

export function anyModule(mods) {
    return ctx => mods.some(m => hasModule(m)(ctx));
}

export function and(...fns) {
    return ctx => fns.every(fn => fn(ctx));
}

export function or(...fns) {
    return ctx => fns.some(fn => fn(ctx));
}

export function not(fn) {
    return ctx => !fn(ctx);
}

/**
 * Heuristic for "volunteer-like" users (no is_volunteer flag on User).
 * Matches default Volunteer/User templates: can claim/renounce tasks, without
 * team-admin powers (users.readOther, claim_for_others, assign, …).
 */
export function isVolunteerLikeUser(user) {
    if (!user?.id)
        return false;
    const ab = user.abilities;
    if (!ab || ab.superadmin)
        return false;
    const tasks = ab.tasks || {};
    const usersAb = ab.users || {};
    if (!(tasks.claim || tasks.renounce))
        return false;
    if (usersAb.readOther || usersAb.editOther || usersAb.create)
        return false;
    if (tasks.claim_for_others || tasks.renounce_for_others || tasks.assign)
        return false;
    return true;
}

const hasEventival = ({ tkt_instance, setting }) => !!tkt_instance?.hasEventivalIntegration?.(setting);
const hasFiona = ({ tkt_instance, setting }) => !!tkt_instance?.hasFionaIntegration?.(setting);
const hasCinech = ({ tkt_instance, setting }) => !!tkt_instance?.hasCinechIntegration?.(setting);
const distributorsMenuVisible = ({ setting }) =>
    Utils.distributorsMenuVisibleForCustomerType(setting?.customer?.type);
const hasPosthog = ({ setting }) => {
    const ph = setting?.integrations?.posthog || {};
    return !!(
        (ph.personal_api_key && String(ph.personal_api_key).trim())
        || (ph.project_api_key && String(ph.project_api_key).trim())
    );
};
const hasJson = ({ tkt_instance, setting }) => !!tkt_instance?.hasJsonIntegration?.(setting);
const hasOzu = ({ tkt_instance, setting }) => !!tkt_instance?.hasOzuIntegration?.(setting);
const votesEnabled = ({ setting }) => setting?.votes?.enabled === true;

// When programming is sourced from an external integration, some planning screens
// become read-only / irrelevant and are hidden from navigation.
const hasExternalProgrammingIntegration = or(hasEventival, hasFiona, hasCinech, hasJson, hasOzu);
const notExternalProgrammingIntegration = not(hasExternalProgrammingIntegration);

const canAnyAbility = mods => ({ user }) => {
    if (!user?.abilities)
        return false;
    if (user.abilities.superadmin)
        return true;
    return mods.some(mod => {
        const a = user.abilities[mod];
        if (!a)
            return false;
        return Object.values(a).some(Boolean);
    });
};

export const NAVIGATION = [
    // ─── Administration ──────────────────────────────────────────────────────
    {
        sectionId: Utils.SECTION_ADMIN,
        groups: [
            {
                id: 'admin.events',
                labelKey: 'app.events',
                iconClass: 'fa fa-clock',
                visible: and(
                    hasModule(Utils.MODULE_PLANNING),
                    canAnyAbility(['programmations', 'events', 'sections', 'places', 'distributors'])
                ),
                items: [
                    {
                        id: 'admin.programmations',
                        path: '/programmation_groups',
                        labelKey: 'app.programmations',
                        iconClass: 'fa fa-calendar-plus',
                        visible: and(notExternalProgrammingIntegration, can('programmations:read')),
                        descKey: 'common.command_palette.programmations_desc',
                        keywords: [
                            'programmation', 'programmations', 'programs', 'programme',
                            'recurrence', 'récurrent', 'lots', 'batch', 'planning',
                            'generate', 'generation', 'séances'
                        ]
                    },
                    {
                        id: 'admin.events.movie',
                        path: '/events/movie',
                        labelKey: 'app.events_movies',
                        iconClass: 'fa fa-film',
                        visible: and(or(notExternalProgrammingIntegration, hasOzu), can('events:read')),
                        descKey: 'common.command_palette.events_movies_desc',
                        keywords: [
                            'films', 'movies', 'filme', 'cinéma',
                            'trailer', 'video', 'affiche', 'poster',
                            'distribution', 'cast', 'réalisateur', 'director'
                        ]
                    },
                    {
                        id: 'admin.events.music_group',
                        path: '/events/music_group',
                        labelKey: 'app.events_music_groups',
                        iconClass: 'fa fa-music',
                        visible: and(notExternalProgrammingIntegration, can('events:read')),
                        descKey: 'common.command_palette.events_music_groups_desc',
                        keywords: [
                            'groupes', 'artistes', 'artists', 'bands', 'künstler',
                            'music', 'musique', 'musik', 'concerts', 'biographie', 'bio'
                        ]
                    },
                    {
                        id: 'admin.events.drama',
                        path: '/events/drama',
                        labelKey: 'app.events_drama',
                        iconClass: 'fa fa-theater-masks',
                        visible: and(notExternalProgrammingIntegration, can('events:read')),
                        descKey: 'common.command_palette.events_drama_desc',
                        keywords: [
                            'théâtre', 'theater', 'pièces',
                            'plays', 'drama', 'stücke', 'représentations'
                        ]
                    },
                    {
                        id: 'admin.events.exhibition',
                        path: '/events/exhibition',
                        labelKey: 'app.events_exhibition',
                        iconClass: 'fa fa-image',
                        visible: and(notExternalProgrammingIntegration, can('events:read')),
                        descKey: 'common.command_palette.events_exhibition_desc',
                        keywords: [
                            'expositions', 'exhibitions', 'ausstellungen',
                            'art', 'œuvres', 'oeuvres', 'works', 'visites', 'créneaux'
                        ]
                    },
                    {
                        id: 'admin.events.service',
                        path: '/events/service',
                        labelKey: 'app.other_activites',
                        iconClass: 'fa fa-gift',
                        visible: and(notExternalProgrammingIntegration, can('events:read')),
                        descKey: 'common.command_palette.events_other_desc',
                        keywords: [
                            'activités', 'activities', 'aktivitäten',
                            'autres', 'divers', 'services', 'prestations',
                            'ateliers', 'workshops', 'visites', 'guided tours'
                        ]
                    },
                    {
                        id: 'admin.sections',
                        path: '/sections',
                        labelKey: 'app.sections',
                        iconClass: 'fa fa-indent',
                        visible: and(notExternalProgrammingIntegration, can('sections:read')),
                        descKey: 'common.command_palette.sections_desc',
                        keywords: [
                            'sections', 'sektionen', 'categories', 'classement',
                            'compétition', 'programme', 'jeune public'
                        ]
                    },
                    {
                        id: 'admin.distributors',
                        path: '/distributors',
                        labelKey: 'app.distributors',
                        iconClass: 'fa fa-money',
                        visible: and(
                            distributorsMenuVisible,
                            or(notExternalProgrammingIntegration, hasCinech, hasOzu),
                            can('distributors:read')
                        ),
                        descKey: 'common.command_palette.distributors_desc',
                        keywords: [
                            'distributeurs', 'compagnies', 'verleih', 'partenaires', 'partners', 'partner',
                            'fournisseurs', 'contrats', 'contracts', 'companies', 'theater'
                        ]
                    },
                    {
                        id: 'admin.places',
                        path: '/places',
                        labelKey: 'app.places',
                        iconClass: 'fa fa-th',
                        visible: can('places:read'),
                        descKey: 'common.command_palette.places_desc',
                        keywords: [
                            'salles', 'halls', 'säle', 'venues', 'lieux',
                            'plans de salle', 'plan', 'plans',
                            'zones', 'placement', 'catégories de sièges', 'capacités', 'capacity'
                        ]
                    }
                ]
            },
            {
                id: 'admin.publishing',
                labelKey: 'app.publishing',
                iconClass: 'fa fa-commenting-o',
                visible: and(
                    hasModule(Utils.MODULE_PUBLISHING),
                    canAnyAbility(['posts', 'channels'])
                ),
                items: [
                    {
                        id: 'admin.posts',
                        path: '/posts',
                        labelKey: 'app.posts',
                        iconClass: 'fa fa-newspaper-o',
                        visible: can('posts:read'),
                        descKey: 'common.command_palette.posts_desc',
                        keywords: [
                            'posts', 'articles', 'news', 'actualités',
                            'pages', 'éditeur', 'editor', 'content',
                            'publication', 'planification', 'schedule'
                        ]
                    },
                    {
                        id: 'admin.channels',
                        path: '/channels',
                        labelKey: 'app.channels',
                        iconClass: 'fa fa-road',
                        visible: can('channels:read'),
                        descKey: 'common.command_palette.channels_desc',
                        keywords: [
                            'canaux', 'channels', 'kanäle',
                            'site', 'website', 'mobile', 'application mobile',
                            'newsletter', 'partenaires', 'diffusion'
                        ]
                    },
                    {
                        id: 'admin.post_categories',
                        path: '/post_categories',
                        labelKey: 'app.categories',
                        iconClass: 'fa fa-folder-o',
                        visible: can('post_categories:read'),
                        sidebarHidden: true,
                        descKey: 'common.command_palette.post_categories_desc',
                        keywords: [
                            'catégories', 'kategorien', 'classement',
                            'posts', 'articles', 'news', 'navigation'
                        ]
                    }
                ]
            },
            {
                id: 'admin.sale',
                labelKey: 'app.sale',
                iconClass: 'fa fa-shopping-basket',
                // Heading shown only when at least one related module is enabled.
                visible: and(
                    or(hasModule(Utils.MODULE_SHOP), hasModule(Utils.MODULE_PLANNING)),
                    canAnyAbility(['articles', 'tickettypes', 'pricinglists', 'bucketlists'])
                ),
                items: [
                    {
                        id: 'admin.pricinglists',
                        path: '/pricinglists',
                        labelKey: 'common.pricinglist',
                        labelOptions: { count: 42 },
                        iconClass: 'fa fa-dollar-sign',
                        visible: and(hasModule(Utils.MODULE_PLANNING), can('pricinglists:read')),
                        descKey: 'common.command_palette.pricinglists_desc',
                        keywords: [
                            'grilles', 'tarifaires', 'tarifs', 'prix',
                            'pricing', 'pricelist', 'preise', 'preisliste'
                        ]
                    },
                    {
                        id: 'admin.tickettypes',
                        path: '/tickettypes',
                        labelKey: 'common.tickettype',
                        labelOptions: { count: 42 },
                        iconClass: 'fa fa-tags',
                        visible: and(hasModule(Utils.MODULE_PLANNING), can('tickettypes:read')),
                        descKey: 'common.command_palette.tickettypes_desc',
                        keywords: [
                            'types', 'billets', 'tickets', 'tickettypen',
                            'abonnements', 'subscriptions',
                            'invitation', 'invitations', 'einladung', 'einladungen',
                            'accréditation', 'accréditations',
                            'akkreditierung', 'akkreditierungen',
                            'bons', 'vouchers', 'gift cards'
                        ]
                    },
                    {
                        id: 'admin.bucketlists',
                        path: '/bucketlists',
                        labelKey: 'common.bucketlist',
                        labelOptions: { count: 42 },
                        iconClass: 'fa fa-battery-half',
                        visible: and(hasModule(Utils.MODULE_PLANNING), can('bucketlists:read')),
                        descKey: 'common.command_palette.bucketlists_desc',
                        keywords: [
                            'contingents', 'quotas', 'buckets', 'bucketlists',
                            'capacité', 'capacites', 'capacity', 'kapazität',
                            'overbooking', 'no-show'
                        ]
                    },
                    {
                        id: 'admin.articles',
                        path: '/articles',
                        labelKey: 'app.articles_and_discount',
                        iconClass: 'fa fa-list-ul',
                        visible: and(hasModule(Utils.MODULE_SHOP), can('articles:read')),
                        descKey: 'common.command_palette.articles_desc',
                        keywords: [
                            'articles', 'artikel', 'products',
                            'boissons', 'nourriture', 'food', 'drinks',
                            'goodies', 'merchandising', 'merch',
                            'rabais', 'discount', 'remises'
                        ]
                    },
                    {
                        id: 'admin.articlecategories',
                        path: '/articlecategories',
                        labelKey: 'app.categories',
                        iconClass: 'fa fa-folder-o',
                        visible: and(hasModule(Utils.MODULE_SHOP), can('articlecategories:read')),
                        sidebarHidden: true,
                        descKey: 'common.command_palette.articlecategories_desc',
                        keywords: [
                            'catégories', 'kategorien',
                            'bar', 'goodies', 'merchandising', 'articles'
                        ]
                    }
                ]
            },
            {
                id: 'admin.templates_and_media',
                labelKey: 'app.templates_and_media',
                iconClass: 'fa fa-picture-o',
                visible: or(
                    and(hasModule(Utils.MODULE_PLANNING), or(can('pdf_layouts:any'), can('email_layouts:any'))),
                    can('settings:any')
                ),
                items: [
                    {
                        id: 'admin.layouts',
                        path: '/layouts',
                        labelKey: 'app.layouts',
                        iconClass: 'fa fa-file-pdf-o',
                        visible: and(hasModule(Utils.MODULE_PLANNING), can('pdf_layouts:any')),
                        descKey: 'common.command_palette.layouts_desc',
                        keywords: [
                            'modèles', 'templates', 'vorlagen',
                            'pdf', 'impression', 'print', 'layout', 'layouts',
                            'codes barres', 'barcodes', 'qr'
                        ]
                    },
                    {
                        id: 'admin.email_layouts',
                        path: '/email_layouts',
                        labelKey: 'app.email_layouts',
                        iconClass: 'fa fa-envelope',
                        visible: and(hasModule(Utils.MODULE_PLANNING), can('email_layouts:any')),
                        descKey: 'common.command_palette.email_layouts_desc',
                        keywords: [
                            'modèles', 'templates', 'vorlagen',
                            'e-mails', 'emails', 'mail', 'campagnes', 'campaigns',
                            'transactionnels'
                        ]
                    },
                    {
                        id: 'admin.graphic_elements',
                        path: '/settings/edit/default/images',
                        labelKey: 'app.images_and_fonts',
                        iconClass: 'fa fa-font',
                        visible: can('settings:any'),
                        descKey: 'common.command_palette.images_and_fonts_desc',
                        keywords: [
                            'logos', 'logo', 'favicon', 'couleurs', 'colors', 'farben',
                            'polices', 'fonts', 'schriftarten', 'typography',
                            'arrière-plans', 'backgrounds',
                            'identité visuelle', 'branding', 'images'
                        ]
                    }
                ]
            },
            {
                id: 'admin.teams',
                labelKey: 'app.teams',
                iconClass: 'fa fa-users',
                visible: canAnyAbility([
                    'users', 'ticketack_users', 'resources', 'activities', 'application_types'
                ]),
                items: [
                    {
                        id: 'admin.users',
                        path: '/users',
                        labelKey: 'app.users',
                        iconClass: 'fa fa-cogs',
                        visible: can('users:read'),
                        descKey: 'common.command_palette.users_desc',
                        keywords: [
                            'utilisateurs', 'users', 'benutzer',
                            'collaborateurs', 'collaborators', 'mitarbeiter',
                            'rôles', 'permissions', 'droits'
                        ]
                    },
                    {
                        id: 'admin.ticketack_users',
                        path: '/ticketack_users',
                        labelKey: 'app.ticketack_users',
                        iconClass: 'fa fa-users',
                        visible: can('ticketack_users:read'),
                        descKey: 'common.command_palette.ticketack_users_desc',
                        keywords: [
                            'ticketack users', 'application users', 'pos users',
                            'caisse', 'pos', 'access control', 'contrôle d\'accès',
                            'kiosque', 'kiosk', 'bornes'
                        ]
                    },
                    {
                        id: 'admin.activities',
                        path: '/activities',
                        labelKey: 'app.activities',
                        iconClass: 'fa fa-id-badge',
                        visible: and(
                            or(hasModule(Utils.MODULE_RESOURCES), hasModule(Utils.MODULE_TEAM)),
                            can('activities:read')
                        ),
                        descKey: 'common.command_palette.activities_desc',
                        keywords: [
                            'fonctions', 'fonction', 'functions', 'function',
                            'activities', 'funktionen', 'funktion',
                            'collaborateurs', 'staff', 'barman', 'caissier'
                        ]
                    },
                    {
                        id: 'admin.application_types',
                        path: '/application_types',
                        labelKey: 'app.application_types',
                        iconClass: 'fa fa-address-book',
                        visible: and(hasModule(Utils.MODULE_TEAM), can('application_types:read')),
                        descKey: 'common.command_palette.application_types_desc',
                        keywords: [
                            'missions', 'candidatures', 'applications',
                            'bénévolat', 'volunteering',
                            'stage', 'internship', 'prestataire'
                        ]
                    },
                    {
                        id: 'admin.resources',
                        path: '/resources',
                        labelKey: 'app.resources_keys',
                        iconClass: 'fa fa-key',
                        visible: can('resources:read'),
                        descKey: 'common.command_palette.resources_desc',
                        keywords: [
                            'clés', 'keys', 'schlüssel',
                            'ressources', 'resources', 'matériel',
                            'inventaire', 'inventory', 'prêt', 'lending'
                        ]
                    }
                ]
            },
            {
                id: 'admin.company',
                labelKey: 'app.company',
                iconClass: 'fa fa-bolt',
                visible: canAnyAbility([
                    'settings', 'salepoints', 'cashregisters', 'editions', 'phases', 'periods',
                    'sectors', 'form_layouts', 'workflows'
                ]),
                items: [
                    {
                        id: 'admin.salepoints',
                        path: '/salepoints',
                        labelKey: 'common.salepoint',
                        labelOptions: { count: 42 },
                        iconClass: 'fa fa-map-marker',
                        visible: and(
                            or(hasModule(Utils.MODULE_SHOP), hasModule(Utils.MODULE_PLANNING)),
                            can('salepoints:read')
                        ),
                        descKey: 'common.command_palette.salepoints_desc',
                        keywords: [
                            'points de vente', 'salepoints', 'pos', 'kasse',
                            'paiement', 'payments', 'logo', 'reçus', 'receipts'
                        ]
                    },
                    {
                        id: 'admin.cashregisters',
                        path: '/cashregisters',
                        labelKey: 'common.cashregister',
                        labelOptions: { count: 42 },
                        iconClass: 'fa fa-th',
                        visible: and(
                            or(hasModule(Utils.MODULE_SHOP), hasModule(Utils.MODULE_PLANNING)),
                            can('cashregisters:read')
                        ),
                        descKey: 'common.command_palette.cashregisters_desc',
                        keywords: [
                            'caisses', 'cash registers', 'registers', 'kassen',
                            'ouverture', 'clôture', 'comptabilité'
                        ]
                    },
                    {
                        id: 'admin.editions',
                        path: '/editions',
                        labelKey: 'app.editions',
                        iconClass: 'fa fa-calendar',
                        visible: can('editions:read'),
                        descKey: 'common.command_palette.editions_desc',
                        keywords: [
                            'éditions', 'ausgaben', 'saison',
                            'festival', 'année', 'year'
                        ]
                    },
                    {
                        id: 'admin.phases',
                        path: '/phases',
                        labelKey: 'app.phases',
                        iconClass: 'fa fa-arrows-h',
                        visible: and(hasModule(Utils.MODULE_TEAM), can('phases:read')),
                        descKey: 'common.command_palette.phases_desc',
                        keywords: [
                            'phases', 'étapes', 'stages',
                            'montage', 'festival', 'démontage', 'teardown'
                        ]
                    },
                    {
                        id: 'admin.periods',
                        path: '/periods',
                        labelKey: 'app.periods',
                        iconClass: 'fa fa-sun-o',
                        visible: and(hasModule(Utils.MODULE_TEAM), can('periods:read')),
                        descKey: 'common.command_palette.periods_desc',
                        keywords: [
                            'créneaux horaires', 'créneau horaire', 'périodes', 'periods', 'zeiträume',
                            'matin', 'midi', 'soir', 'morning', 'evening'
                        ]
                    },
                    {
                        id: 'admin.sectors',
                        path: '/sectors',
                        labelKey: 'app.sectors',
                        iconClass: 'fa fa-location-arrow',
                        visible: and(hasModule(Utils.MODULE_TEAM), can('sectors:read')),
                        descKey: 'common.command_palette.sectors_desc',
                        keywords: [
                            'secteurs', 'sectors', 'sektoren',
                            'bars', 'accueil', 'reception', 'billetterie', 'ticketing'
                        ]
                    },
                    {
                        id: 'admin.form_layouts',
                        path: '/form_layouts',
                        labelKey: 'app.form_layouts',
                        iconClass: 'fa fa-check-square-o',
                        visible: and(
                            or(hasModule(Utils.MODULE_TEAM), hasModule(Utils.MODULE_WORKFLOWS)),
                            can('form_layouts:read')
                        ),
                        descKey: 'common.command_palette.form_layouts_desc',
                        keywords: [
                            'formulaires', 'forms', 'formulare',
                            'modèles', 'templates', 'custom'
                        ]
                    },
                    {
                        id: 'admin.workflows',
                        path: '/workflows',
                        labelKey: 'app.workflows',
                        iconClass: 'fa fa-code-fork',
                        visible: and(hasModule(Utils.MODULE_WORKFLOWS), can('workflows:read')),
                        descKey: 'common.command_palette.workflows_desc',
                        keywords: [
                            'workflows', 'automation', 'automatisation', 'automatisierung',
                            'déclencheurs', 'triggers', 'actions', 'chaîne'
                        ]
                    }
                ]
            },
            {
                id: 'admin.equipment',
                labelKey: 'equipment.title',
                iconClass: 'fa fa-wrench',
                visible: canAnyAbility(['equipment_orders', 'configuration_qr_codes', 'devices']),
                items: [
                    {
                        id: 'admin.devices',
                        path: '/devices',
                        labelKey: 'equipment.menu_management',
                        iconClass: 'fa fa-cubes',
                        visible: can('devices:any'),
                        descKey: 'common.command_palette.devices_desc',
                        keywords: [
                            'matériel', 'equipment', 'ausrüstung',
                            'devices', 'appareils', 'geräte',
                            'caisses', 'imprimantes', 'printers', 'scanners', 'bornes', 'kiosks',
                            'parc', 'fleet', 'jumelage', 'pairing',
                            'raspberry pi', 'raspberry',
                            'imprimante à carte', 'imprimante carte', 'card printer', 'zebra',
                            'pax', 'tpe', 'eft', 'terminal', 'terminal de paiement'
                        ]
                    },
                    {
                        id: 'admin.configuration_qr_codes',
                        path: '/configuration_qr_codes',
                        labelKey: 'equipment.menu_qr_codes',
                        iconClass: 'fa fa-qrcode',
                        visible: can('configuration_qr_codes:any'),
                        descKey: 'common.command_palette.configuration_qr_codes_desc',
                        keywords: [
                            'qr', 'qrcode', 'qr codes', 'codes qr',
                            'configuration', 'scan', 'mode',
                            'caisse', 'contrôle d\'accès', 'controle', 'borne',
                            'matériel', 'equipment'
                        ]
                    },
                    {
                        id: 'admin.equipment_orders',
                        path: '/equipment_orders',
                        labelKey: 'equipment.menu_orders',
                        iconClass: 'fa fa-list',
                        visible: can('equipment_orders:any'),
                        descKey: 'common.command_palette.equipment_orders_desc',
                        keywords: [
                            'commandes', 'orders', 'bestellungen',
                            'matériel', 'equipment',
                            'livraison', 'shipping', 'ticketack'
                        ]
                    }
                ]
            },
            {
                id: 'admin.settings',
                labelKey: 'app.settings',
                iconClass: 'fa fa-gear',
                visible: canAnyAbility(['settings', 'cron_tasks']),
                items: [
                    {
                        id: 'admin.settings.main',
                        path: '/settings/edit/default',
                        labelKey: 'app.settings',
                        iconClass: 'fa fa-gear',
                        visible: can('settings:any'),
                        descKey: 'common.command_palette.settings_desc',
                        keywords: [
                            'paramètres', 'settings', 'einstellungen',
                            'configuration', 'config',
                            'modules'
                        ]
                    },
                    {
                        id: 'admin.settings.customer',
                        path: '/settings/edit/default/customer',
                        labelKey: 'settings.customer.title',
                        iconClass: 'fa fa-building',
                        visible: can('settings:any'),
                        sidebarHidden: true,
                        descKey: 'common.command_palette.settings_customer_desc',
                        keywords: [
                            'vos informations', 'your information', 'ihre informationen',
                            'organisateur', 'organisation', 'structure', 'coordonnées',
                            'tva', 'vat', 'mwst', 'taxes', 'taxe', 'steuer',
                            'suisa', 'SUISA',
                            'impôt local', 'local tax', 'lokale steuer',
                            'numéro de tva', 'ust-idnr'
                        ]
                    },
                    {
                        id: 'admin.settings.default_layouts',
                        path: '/settings/edit/default/default_layouts',
                        labelKey: 'settings.default_layouts.title',
                        iconClass: 'fa fa-file-alt',
                        visible: can('settings:any'),
                        descKey: 'common.command_palette.settings_default_layouts_desc',
                        keywords: [
                            'modèles par défaut', 'default templates', 'standardvorlagen',
                            'email_layouts', 'pdf_layouts', 'confirmation', 'quittance'
                        ]
                    },
                    {
                        id: 'admin.settings.l10n',
                        path: '/settings/edit/default/l10n',
                        labelKey: 'settings.l10n.title',
                        iconClass: 'fa fa-globe',
                        visible: can('settings:any'),
                        sidebarHidden: true,
                        descKey: 'common.command_palette.settings_l10n_desc',
                        keywords: [
                            'localisation', 'localization', 'lokalisierung', 'l10n',
                            'euro', 'eur', '€',
                            'devise', 'devises', 'monnaie', 'currency', 'währung',
                            'langue', 'langues', 'languages', 'sprachen', 'locales',
                            'timezone', 'fuseau', 'zeitzone'
                        ]
                    },
                    {
                        id: 'admin.settings.integrations',
                        path: '/settings/edit/default/integrations',
                        labelKey: 'settings.integrations.title',
                        iconClass: 'fa fa-plug',
                        visible: can('settings:any'),
                        descKey: 'common.command_palette.settings_integrations_desc',
                        keywords: [
                            'intégrations', 'integrations', 'integrationen',
                            'api', 'webhooks',
                            'url', 'wordpress', 'json', 'gtm', 'google tag manager',
                            'posthog', 'plausible', 'analytics',
                            'eventival', '1o1', 'ownit', 'shift72', 'pantaflix',
                            'fiona', 'mailchimp', 'brevo', 'getresponse', 'maccsbox', 'festivalscope'
                        ]
                    },
                    {
                        id: 'admin.settings.pos',
                        path: '/settings/edit/default/pos',
                        labelKey: 'settings.pos.title',
                        iconClass: 'fa fa-ticket',
                        visible: can('settings:any'),
                        sidebarHidden: true,
                        descKey: 'common.command_palette.settings_pos_desc',
                        keywords: [
                            'vente sur place', 'point de vente', 'pdv',
                            'verkauf vor ort', 'on-site sales', 'pos',
                            'caisse', 'kasse', 'cash register',
                            'politique d\'annulation', 'politique d\'annulation réservation',
                            'booking cancellation policy', 'cancellation policy',
                            'stornierung', 'stornierungsrichtlinie',
                            'annulation', 'remboursement', 'refund', 'rückerstattung',
                            'réservations', 'impression automatique'
                        ]
                    },
                    {
                        id: 'admin.settings.eshop',
                        path: '/settings/edit/default/eshop',
                        labelKey: 'settings.eshop.title',
                        iconClass: 'fa fa-shopping-cart',
                        visible: can('settings:any'),
                        sidebarHidden: true,
                        descKey: 'common.command_palette.settings_eshop_desc',
                        keywords: [
                            'vente en ligne', 'e-shop', 'eshop', 'boutique en ligne',
                            'online sales', 'online-verkauf', 'shop',
                            'politique d\'annulation', 'politique d\'annulation réservation',
                            'booking cancellation policy', 'cancellation policy',
                            'buchungsstornierung', 'panier', 'cart', 'warenkorb',
                            'iframe', 'newsletter', 'inscription', 'registration'
                        ]
                    },
                    {
                        id: 'admin.settings.payment',
                        path: '/settings/edit/default/payment',
                        labelKey: 'settings.payment.title',
                        iconClass: 'fa fa-credit-card',
                        visible: can('settings:any'),
                        sidebarHidden: true,
                        descKey: 'common.command_palette.settings_payment_desc',
                        keywords: [
                            'paiements', 'paiement', 'payment', 'zahlung',
                            'moyens de paiement', 'means of payment', 'zahlungsmethoden',
                            'twint', 'pimpay', 'sumup', 'proxypay', 'carte', 'card',
                            'espèces', 'cash', 'barzahlung'
                        ]
                    },
                    {
                        id: 'admin.settings.delivery',
                        path: '/settings/edit/default/delivery',
                        labelKey: 'settings.delivery.title',
                        iconClass: 'fa fa-truck',
                        visible: can('settings:any'),
                        sidebarHidden: true,
                        descKey: 'common.command_palette.settings_delivery_desc',
                        keywords: [
                            'livraison', 'delivery', 'lieferung',
                            'à emporter', 'takeaway', 'abholung',
                            'envoi', 'shipment', 'versand',
                            'sur place', 'pickup',
                            'modes de livraison', 'shipping methods'
                        ]
                    },
                    {
                        id: 'admin.settings.votes',
                        path: '/settings/edit/default/votes',
                        labelKey: 'settings.votes.title',
                        iconClass: 'fa fa-star',
                        visible: can('settings:any'),
                        sidebarHidden: true,
                        descKey: 'common.command_palette.settings_votes_desc',
                        keywords: [
                            'votes', 'vote', 'abstimmung', 'bewertung',
                            'notations', 'notes', 'étoiles', 'stars',
                            'rating', 'rating poll'
                        ]
                    },
                    {
                        id: 'admin.cron_tasks',
                        path: '/cron_tasks',
                        labelKey: 'app.cron_tasks',
                        iconClass: 'fa fa-tasks',
                        visible: can('cron_tasks:any'),
                        descKey: 'common.command_palette.cron_tasks_desc',
                        keywords: [
                            'cron', 'tâches', 'tasks', 'aufgaben',
                            'planifié', 'scheduled',
                            'imports', 'synchronisations', 'rapports', 'cleanup',
                            'accréditations eventival',
                            'eventival', 'maccsbox'
                        ]
                    },
                    {
                        id: 'admin.mobile_settings',
                        path: '/settings/edit/default/mobile',
                        labelKey: 'settings.mobile.title',
                        iconClass: 'fa fa-mobile',
                        visible: can('settings:any'),
                        descKey: 'common.command_palette.mobile_settings_desc',
                        keywords: [
                            'mobile', 'application mobile', 'app', 'ticketack app',
                            'mobile app', 'app mobile', 'smartphone', 'tablette', 'tablet'
                        ]
                    },
                    {
                        id: 'admin.retention',
                        path: '/settings/edit/default/retention',
                        labelKey: 'settings.retention.title',
                        iconClass: 'fa fa-shield',
                        visible: can('settings:any'),
                        descKey: 'common.command_palette.retention_desc',
                        keywords: [
                            'rétention', 'lpd', 'rgpd', 'gdpr', 'dsgvo', 'dsg',
                            'données personnelles', 'personal data', 'datenschutz',
                            'durée', 'duration', 'conservation',
                            'droit à l\'oubli',
                            'droit à l\'effacement'
                        ]
                    }
                ]
            }
        ]
    },

    // ─── Operation ───────────────────────────────────────────────────────────
    {
        sectionId: Utils.SECTION_OPERATION,
        groups: [
            {
                id: 'op.clients',
                labelKey: 'app.tickets_clients',
                iconClass: 'fa fa-user',
                visible: canAnyAbility(['customers', 'screenings', 'tickets']),
                items: [
                    {
                        id: 'op.customers',
                        path: '/customers',
                        labelKey: 'common.customer',
                        labelOptions: { count: 42 },
                        iconClass: 'fa fa-users',
                        visible: can('customers:read'),
                        descKey: 'common.command_palette.customers_desc',
                        keywords: [
                            'clients', 'customers', 'kunden', 'fiches', 'contacts',
                            'e-portefeuille', 'ewallet', 'wallet',
                            'abonnements', 'subscriptions',
                            'historique', 'achats',
                            'crm'
                        ]
                    },
                    {
                        id: 'op.screenings',
                        path: '/screenings',
                        labelKey: 'common.screening',
                        labelOptions: { count: 42 },
                        iconClass: 'fa fa-television',
                        visible: and(hasModule(Utils.MODULE_PLANNING), can('screenings:read')),
                        descKey: 'common.command_palette.screenings_desc',
                        keywords: [
                            'séances', 'screenings', 'shows', 'sessions',
                            'concerts', 'représentations',
                            'vorstellungen', 'aktivitäten',
                            'contingents', 'quotas', 'réservations'
                        ]
                    },
                    {
                        id: 'op.tickets',
                        path: '/tickets',
                        labelKey: 'common.ticket',
                        labelOptions: { count: 42 },
                        iconClass: 'fa fa-tags',
                        visible: and(hasModule(Utils.MODULE_PLANNING), can('tickets:read')),
                        descKey: 'common.command_palette.tickets_desc',
                        keywords: [
                            'billets', 'tickets',
                            'recherche', 'search',
                            'statut', 'status',
                            'imports', 'batch', 'lots',
                            'réservations'
                        ]
                    },
                    {
                        id: 'op.reservations',
                        path: '/reservations',
                        labelKey: 'statstypes.reservations',
                        iconClass: 'fa fa-ticket',
                        visible: and(hasModule(Utils.MODULE_PLANNING), can('screenings:read')),
                        descKey: 'common.command_palette.stats_reservations_desc',
                        keywords: [
                            'stats', 'statistiques', 'réservations', 'reservations', 'bookings',
                            'occupation', 'scans', 'état des réservations', 'booking status'
                        ]
                    }
                ]
            },
            {
                id: 'op.teams',
                labelKey: 'app.teams',
                iconClass: 'fa fa-users',
                // Visible if any classic item is visible, or Mon profil for volunteers.
                visible: ctx => {
                    const user = ctx.user;
                    const tkt_instance = ctx.tkt_instance;
                    if (!user || !tkt_instance)
                        return false;
                    if (isVolunteerLikeUser(user))
                        return true;
                    const ab = user.abilities;
                    const su = !!ab?.superadmin;
                    const tasks = tkt_instance.hasModule(Utils.MODULE_RESOURCES)
                        && (su || (ab?.tasks && (ab.tasks.readSelf || ab.tasks.readOther)));
                    const plan = tkt_instance.hasModule(Utils.MODULE_TEAM)
                        && (su || (ab?.plan && (ab.plan.readSelf || ab.plan.readOther)));
                    const applications = tkt_instance.hasModule(Utils.MODULE_TEAM)
                        && (su || (ab?.applications && (ab.applications.readSelf || ab.applications.readOther)));
                    const messages = tkt_instance.hasModule(Utils.MODULE_COMMUNICATIONS)
                        && (su || !!ab?.messages?.create);
                    return tasks || plan || applications || messages;
                },
                items: [
                    {
                        id: 'op.tasks',
                        path: '/tasks',
                        labelKey: 'app.planning',
                        iconClass: 'fa fa-calendar',
                        visible: and(hasModule(Utils.MODULE_RESOURCES), can('tasks:read')),
                        descKey: 'common.command_palette.tasks_desc',
                        keywords: [
                            'tâches', 'tasks', 'aufgaben',
                            'postes', 'planning', 'planification',
                            'équipes', 'teams', 'ressources', 'resources'
                        ]
                    },
                    {
                        id: 'op.plan',
                        path: '/plan',
                        labelKey: 'app.plan',
                        iconClass: 'fa fa-calendar-plus',
                        visible: or(
                            and(hasModule(Utils.MODULE_TEAM), can('plan:read')),
                            and(hasModule(Utils.MODULE_RESOURCES), can('tasks:read')),
                        ),
                        descKey: 'common.command_palette.plan_desc',
                        keywords: [
                            'plan', 'planning', 'présence', 'attendance',
                            'collaborateurs', 'équipes', 'teams',
                            'calendrier', 'calendar',
                            'phase', 'secteur', 'sector', 'période',
                            'séances', 'screenings', 'postes', 'salles', 'places'
                        ]
                    },
                    {
                        id: 'op.applications',
                        path: '/applications',
                        labelKey: 'app.applications',
                        iconClass: 'fa fa-address-book-o',
                        visible: and(hasModule(Utils.MODULE_TEAM), can('applications:read')),
                        descKey: 'common.command_palette.applications_desc',
                        keywords: [
                            'candidatures', 'applications', 'bewerbungen',
                            'bénévoles', 'volunteers',
                            'staff', 'prestataires', 'providers'
                        ]
                    },
                    {
                        id: 'op.messages',
                        path: '/messages',
                        labelKey: 'app.communication',
                        iconClass: 'fa fa-envelope',
                        visible: and(hasModule(Utils.MODULE_COMMUNICATIONS), can('messages:create')),
                        descKey: 'common.command_palette.messages_desc',
                        keywords: [
                            'messages', 'communications', 'communication',
                            'e-mail', 'email', 'sms',
                            'newsletter', 'campagnes', 'campaigns', 'segmentation'
                        ]
                    }
                ],
                dynamicItems: ({ user }) => {
                    if (!isVolunteerLikeUser(user))
                        return [];
                    return [{
                        id: 'op.my_profile',
                        path: `/users/edit/${user.id}/general`,
                        labelKey: 'app.my_profil',
                        iconClass: 'fa fa-user',
                        visible: () => true,
                        descKey: 'common.command_palette.my_profile_desc',
                        keywords: [
                            'profil', 'profile', 'mein profil',
                            'disponibilités', 'availabilities', 'verfügbarkeiten',
                            'bénévol', 'volunteer', 'freiwillige'
                        ]
                    }];
                }
            },
            {
                id: 'op.workflows',
                labelKey: 'app.workflows',
                iconClass: 'fa fa-code-fork',
                visible: and(
                    hasModule(Utils.MODULE_WORKFLOWS),
                    can('workflow_runs:read'),
                    ctx => Array.isArray(ctx.workflows) && ctx.workflows.length > 0
                ),
                items: [],
                dynamicItems: ({ workflows }) => (workflows || []).map(workflow => ({
                    id: `op.workflow_runs.${workflow.id}`,
                    path: `/operation/workflows/${workflow.id}/runs`,
                    label: Utils.localized_or_fallback(workflow.name, Utils.default_lang(), workflow.id),
                    iconClass: 'fa fa-history',
                    className: 'workflow_nav_label',
                    visible: () => true,
                    keywords: ['workflow', 'workflows', 'runs', 'history']
                }))
            }
        ]
    },

    // ─── Analysis & statistics ───────────────────────────────────────────────
    {
        sectionId: Utils.SECTION_ANALYSIS,
        groups: [
            {
                id: 'analysis.stats',
                labelKey: 'app.stats',
                iconClass: 'fa fa-shopping-basket',
                visible: can('stats:read'),
                items: [
                    {
                        id: 'analysis.stats.movies',
                        path: '/stats/movies',
                        labelKey: 'statstypes.movies',
                        iconClass: 'fa fa-film',
                        visible: () => true,
                        descKey: 'common.command_palette.stats_movies_desc',
                        keywords: [
                            'stats', 'statistiques', 'films', 'movies', 'filme',
                            'fréquentation', 'recettes', 'box office par film'
                        ]
                    },
                    {
                        id: 'analysis.stats.prices',
                        path: '/stats/prices',
                        labelKey: 'statstypes.prices',
                        iconClass: 'fa fa-tag',
                        visible: () => true,
                        descKey: 'common.command_palette.stats_prices_desc',
                        keywords: [
                            'stats', 'statistiques', 'tarifs', 'prix', 'prices', 'pricing', 'preise',
                            'ventilation', 'répartition'
                        ]
                    },
                    {
                        id: 'analysis.stats.places',
                        path: '/stats/places',
                        labelKey: 'statstypes.halls',
                        iconClass: 'fa fa-map-marker',
                        visible: () => true,
                        descKey: 'common.command_palette.stats_places_desc',
                        keywords: [
                            'stats', 'statistiques', 'salles', 'halls', 'räume',
                            'remplissage', 'occupation', 'occupancy', 'recettes'
                        ]
                    },
                    {
                        id: 'analysis.stats.days',
                        path: '/stats/days',
                        labelKey: 'statstypes.income_daily',
                        iconClass: 'fa fa-calendar',
                        visible: () => true,
                        descKey: 'common.command_palette.stats_days_desc',
                        keywords: [
                            'stats', 'statistiques', 'jour', 'jours', 'days', 'tage',
                            'fréquentation', 'revenus', 'revenue', 'tagesumsatz'
                        ]
                    },
                    {
                        id: 'analysis.stats.hours',
                        path: '/stats/hours',
                        labelKey: 'statstypes.income_hourly',
                        iconClass: 'fa fa-clock',
                        visible: () => true,
                        descKey: 'common.command_palette.stats_hours_desc',
                        keywords: [
                            'stats', 'statistiques', 'heure', 'heures', 'hours', 'stunden',
                            'fréquentation', 'revenus', 'stundenumsatz', 'pic', 'pics', 'affluence'
                        ]
                    },
                    {
                        id: 'analysis.stats.box_office',
                        path: '/stats/box_office',
                        labelKey: 'statstypes.box_office',
                        iconClass: 'fa fa-star',
                        visible: () => true,
                        descKey: 'common.command_palette.stats_box_office_desc',
                        keywords: [
                            'stats', 'statistiques', 'box office', 'classement',
                            'ranking', 'charts', 'top', 'top ventes', 'bestseller', 'hitparade'
                        ]
                    }
                ]
            },
            {
                id: 'analysis.reports',
                labelKey: 'app.analysis',
                iconClass: 'fa fa-line-chart',
                // Same gating as the stats group in the current SideBar (items live
                // inside the same `can="stats:read"` block).
                visible: can('stats:read'),
                items: [
                    {
                        id: 'analysis.accounting',
                        path: '/stats/accounting',
                        labelKey: 'app.accounting_analysis',
                        iconClass: 'fa fa-money',
                        visible: () => true,
                        descKey: 'common.command_palette.accounting_desc',
                        keywords: [
                            'comptabilité', 'accounting', 'buchhaltung',
                            'flux', 'cash flow', 'recettes', 'chiffre d\'affaires', 'ca', 'revenue', 'revenus',
                            'comparaison', 'périodes',
                            'erp'
                        ]
                    },
                    {
                        id: 'analysis.attendance',
                        path: '/stats/attendance',
                        labelKey: 'app.attendance_analysis',
                        iconClass: 'fa fa-users',
                        visible: () => true,
                        descKey: 'common.command_palette.attendance_desc',
                        keywords: [
                            'fréquentation', 'attendance', 'besucher',
                            'affluence', 'entrées', 'flux', 'personnes',
                            'compteur', 'comparaison', 'périodes'
                        ]
                    },
                    {
                        id: 'analysis.bookings',
                        path: '/analysis/bookings',
                        labelKey: 'statstypes.texts.bookings_analysis',
                        iconClass: 'fa fa-ticket',
                        visible: () => true,
                        descKey: 'common.command_palette.stats_bookings_desc',
                        keywords: [
                            'stats', 'statistiques', 'réservations', 'bookings', 'code postal',
                            'zip', 'scans', 'entrées', 'analyse réservations'
                        ]
                    },
                    {
                        id: 'analysis.email_campaigns',
                        path: '/analysis/email_campaigns',
                        labelKey: 'app.email_campaigns',
                        iconClass: 'fa fa-envelope',
                        visible: can('logs:readOther'),
                        descKey: 'common.command_palette.email_campaigns_desc',
                        keywords: [
                            'campagnes', 'campaigns', 'kampagnen', 'newsletter', 'mailings',
                            'mail', 'e-mail', 'email',
                            'ouverture', 'open rate', 'clics', 'clicks',
                            'désabonnement', 'unsubscribe', 'bounces'
                        ]
                    },
                    {
                        id: 'analysis.web_traffic',
                        path: '/analysis/web_traffic',
                        labelKey: 'app.web_traffic',
                        iconClass: 'fa fa-globe',
                        visible: can('stats:read'),
                        descKey: 'common.command_palette.web_traffic_desc',
                        keywords: [
                            'plausible', 'trafic', 'traffic', 'web',
                            'fréquentation', 'visiteurs', 'visitors',
                            'pageviews', 'analytics'
                        ]
                    },
                    {
                        id: 'analysis.posthog',
                        path: '/analysis/posthog',
                        labelKey: 'app.product_analytics',
                        iconClass: 'fa fa-area-chart',
                        visible: and(can('stats:read'), hasPosthog),
                        descKey: 'common.command_palette.product_analytics_desc',
                        keywords: [
                            'posthog', 'analytics', 'product analytics',
                            'analyse produit', 'statistiques produit',
                            'usage', 'événements', 'events', 'dashboard'
                        ]
                    },
                    {
                        id: 'analysis.votes',
                        path: '/analysis/votes',
                        labelKey: 'statstypes.texts.votes',
                        iconClass: 'fa fa-star-half-o',
                        visible: votesEnabled,
                        descKey: 'statstypes.texts.votes',
                        keywords: [
                            'votes', 'scores', 'notation', 'films', 'export votes'
                        ]
                    }
                ]
            },
            {
                id: 'analysis.email_only',
                labelKey: 'app.analysis',
                iconClass: 'fa fa-line-chart',
                // Shown only when the user has access to logs but not to stats.
                visible: and(not(can('stats:readOther')), can('logs:readOther')),
                items: [
                    {
                        id: 'analysis.email_campaigns.only',
                        path: '/analysis/email_campaigns',
                        labelKey: 'app.email_campaigns',
                        iconClass: 'fa fa-envelope',
                        visible: can('logs:readOther'),
                        descKey: 'common.command_palette.email_campaigns_desc',
                        keywords: [
                            'campagnes', 'campaigns', 'kampagnen', 'newsletter', 'mailings',
                            'mail', 'e-mail', 'email'
                        ]
                    }
                ]
            },
            {
                id: 'analysis.system',
                labelKey: 'app.analysis_system',
                iconClass: 'fa fa-server',
                visible: can('logs:readOther'),
                items: [
                    {
                        id: 'analysis.engine_logs',
                        path: '/analysis/system/logs',
                        labelKey: 'app.engine_logs',
                        iconClass: 'fa fa-history',
                        visible: () => true,
                        descKey: 'common.command_palette.engine_logs_desc',
                        keywords: [
                            'logs', 'log', 'journaux', 'journal', 'moteur', 'engine',
                            'protokoll', 'protokolle',
                            'api', 'erreurs', 'errors', 'fehler',
                            'audit', 'debug', 'trace'
                        ]
                    }
                ]
            }
        ]
    }
];

// Section label i18n keys (match the top bar labels so the breadcrumb uses the
// same wording as the section button users click on).
export const SECTION_LABEL_KEYS = {
    [Utils.SECTION_ADMIN]:     `app.section_${Utils.SECTION_ADMIN}`,
    [Utils.SECTION_OPERATION]: `app.section_${Utils.SECTION_OPERATION}`,
    [Utils.SECTION_ANALYSIS]:  `app.section_${Utils.SECTION_ANALYSIS}`
};

// Visit the navigation tree and return a filtered copy containing only the
// groups/items whose `visible` predicate is satisfied for the given context.
// Dynamic items (e.g. workflow runs) are expanded here.
export function visibleNavigation(ctx) {
    return NAVIGATION
        .map(section => {
            const groups = section.groups
                .map(group => {
                    let items = group.items || [];
                    items = items.filter(item => safeVisible(item, ctx));
                    if (typeof group.dynamicItems === 'function') {
                        const extra = (group.dynamicItems(ctx) || [])
                            .filter(item => safeVisible(item, ctx));
                        // Prepend so user-specific entries (e.g. Mon profil) sit first.
                        items = extra.concat(items);
                    }
                    return { ...group, items };
                })
                .filter(group => safeVisible(group, ctx) && group.items.length > 0);
            return { ...section, groups };
        })
        .filter(section => section.groups.length > 0);
}

function safeVisible(node, ctx) {
    if (node.visible === undefined || node.visible === null)
        return true;
    if (typeof node.visible === 'function')
        return !!node.visible(ctx);
    return !!node.visible;
}

// Return the `{ section, group, item }` triple matching a pathname, or null.
// The lookup walks the visible tree so the breadcrumb reflects the very same
// hierarchy the user actually sees in the sidebar. When several items' paths
// prefix the pathname (e.g. `/settings/edit/default` vs
// `/settings/edit/default/retention`), the most specific (longest) match wins.
export function findItemByPath(pathname, ctx) {
    if (!pathname)
        return null;
    const tree = visibleNavigation(ctx);
    let best = null;
    let best_len = -1;
    for (const section of tree) {
        for (const group of section.groups) {
            for (const item of group.items) {
                if (!item.path)
                    continue;
                if (!pathStartsWith(pathname, item.path))
                    continue;
                if (item.path.length > best_len) {
                    best = { section, group, item };
                    best_len = item.path.length;
                }
            }
        }
    }
    return best;
}

// Match `pathname` against an item `path`: the pathname must equal the path or
// continue with a `/` (avoids spurious matches like /placesXYZ vs /places).
function pathStartsWith(pathname, path) {
    if (pathname === path)
        return true;
    if (!pathname.startsWith(path))
        return false;
    const next = pathname.charAt(path.length);
    return next === '/' || next === '';
}

// Return the list of group ids that contain a route matching `pathname`.
// Used by the sidebar to auto-open the relevant group when the user navigates
// to a page via the command palette or a direct URL.
export function groupIdsForPathname(pathname, ctx) {
    if (!pathname)
        return [];
    const tree = visibleNavigation(ctx);
    const ids = [];
    for (const section of tree) {
        for (const group of section.groups) {
            for (const item of group.items) {
                if (item.path && pathStartsWith(pathname, item.path) && !ids.includes(group.id)) {
                    ids.push(group.id);
                    break;
                }
            }
        }
    }
    return ids;
}
